import React, { useEffect, useState } from "react";
import { useRouter } from "next/router";
import { useSelector } from "react-redux";

import { Action, Category } from "constants/events";
import { Dispensary } from "custom-types/Dispensary";
import useDispensariesLink from "hooks/useDispensariesLink";
import useDomainCountryCode from "hooks/useDomainCountryCode";
import { useEventTracker } from "hooks/useEventTracker";
import useSearch from "redux/hooks/useSearch";
import { getUserId } from "redux/selectors/user";
import { getRoundedDistance } from "utils/distance";

import Carousel from "components/botanic/Carousel";
import DispensaryCard from "components/botanic/DispensaryCard";
import Image from "components/Image";
import TrackImpression from "components/TrackImpression";

const DispensaryResults: React.FC = () => {
  const dispensariesLink = useDispensariesLink();
  const {
    query: { q, searchCategory },
  } = useRouter();

  const { dispensary: dispensaries } = useSearch();

  const countryCode = useDomainCountryCode();
  const userId = useSelector(getUserId);

  const viewingCountOnLoad = searchCategory === "dispensary" ? 12 : 6;
  const [viewingCount, setViewingCount] = useState(viewingCountOnLoad);

  const { publishEvent } = useEventTracker();

  useEffect(() => {
    setViewingCount(viewingCountOnLoad);
  }, [searchCategory]);

  if (!dispensaries || !dispensaries.length) {
    return null;
  }

  return (
    <>
      {searchCategory === "dispensary" ? (
        // Display as grid when filtered to dispensaries
        <>
          <h2 className="heading--l">Locations</h2>
          <span className="font-normal">matching "{q ? String(q) : ""}"</span>
          <div className="row mt-md mb-lg">
            {dispensaries
              .slice(0, viewingCount)
              .map((dispensary: Dispensary) => {
                const {
                  deliveryEnabled,
                  distanceMi,
                  id,
                  logoUrl,
                  name,
                  pickupEnabled,
                  preorderConfigured,
                  rating,
                  reviewCount,
                  slug,
                  tags,
                  timeZone,
                } = dispensary;
                return (
                  <div
                    className="col md:col-1/2 lg:col-1/3 mb-md"
                    key={dispensary.slug}
                  >
                    <div className="h-full rounded elevation-low overflow-hidden">
                      <DispensaryCard
                        preserveHeight
                        currentStatus={dispensary.currentStatuses?.store}
                        deliveryEnabled={deliveryEnabled}
                        distanceMi={distanceMi}
                        hideCTA
                        dispensaryId={dispensary.id}
                        logoUrl={logoUrl}
                        name={name}
                        onClick={() => {
                          publishEvent({
                            action: Action.click,
                            category: Category.retailerCard,
                            dispensaryId: id,
                          });
                        }}
                        pickupEnabled={pickupEnabled}
                        preorderConfigured={preorderConfigured}
                        rating={rating}
                        reviewCount={reviewCount}
                        scheduleType="store"
                        slug={slug}
                        tags={tags}
                        timeZone={timeZone}
                        variant="platinum"
                      />
                    </div>
                  </div>
                );
              })}
          </div>
        </>
      ) : (
        <Carousel
          cardsPerSlide={4}
          title="Locations"
          subtitle={<div className="font-normal mb-md">matching "{q}"</div>}
        >
          {dispensaries.slice(0, viewingCount).map((dispensary: Dispensary) => {
            const {
              deliveryEnabled,
              distanceMi,
              id,
              logoUrl,
              name,
              pickupEnabled,
              rating,
              reviewCount,
              slug,
              tags,
              timeZone,
            } = dispensary;
            return (
              <div
                className="h-full rounded elevation-low overflow-hidden bg-white"
                key={dispensary.slug}
              >
                <TrackImpression
                  key={id}
                  category={Category.retailerCard}
                  className="h-full"
                  customDimensions={{
                    dispensaryId: id,
                  }}
                >
                  <DispensaryCard
                    dispensaryId={dispensary.id}
                    preserveHeight
                    deliveryEnabled={deliveryEnabled}
                    distanceMi={distanceMi}
                    hideCTA
                    inStoreCartEnabled={dispensary.inStoreCartEnabled}
                    logoUrl={logoUrl}
                    name={name}
                    onClick={() => {
                      publishEvent({
                        action: Action.click,
                        category: Category.retailerCard,
                        dispensaryDistance: getRoundedDistance(
                          distanceMi,
                          countryCode
                        ),
                        dispensaryId: id,
                        userId,
                      });
                    }}
                    pickupEnabled={pickupEnabled}
                    rating={rating}
                    reviewCount={reviewCount}
                    slug={slug}
                    tags={tags}
                    timeZone={timeZone}
                    variant="platinum"
                  />
                </TrackImpression>
              </div>
            );
          })}
        </Carousel>
      )}
      <a
        className="button md:mb-xxl mt-lg"
        href={`/search/shop?q=${q ? encodeURIComponent(String(q)) : ""}`}
        onClick={() =>
          publishEvent({
            action: Action.click,
            category: Category.showAllLocationResults,
          })
        }
      >
        show all location results
      </a>

      {
        // desktop link to finder CTA
      }
      <div className="relative text-center hidden md:flex mt-section border-t border-b border-light-grey">
        <Image
          className="hidden md:flex opacity-50"
          sizes={[1025]}
          src="https://leafly-public.imgix.net/global-search/seattle-map-lg.png"
          alt="Example Dispensary Map"
        />
        <div className="finderCTA">
          <div className="text-md text-center">
            Looking to find a nearby dispensary?
          </div>
          <a
            data-testid="view-map-desktop"
            className="button button--tertiary bg-white flex items-center justify-center mt-xl text-sm"
            href={dispensariesLink}
            onClick={() =>
              publishEvent({
                action: Action.click,
                category: Category.viewDispensaryMap,
              })
            }
          >
            view dispensary map
          </a>
        </div>
      </div>

      {
        //mobile link to finder CTA
      }
      <div className="relative mt-section border border-light-grey text-center md:hidden">
        <Image
          className="md:hidden opacity-50"
          sizes={[510, 733, null, null, null, 510]}
          src="https://leafly-public.imgix.net/global-search/seattle-map.png"
          alt="Example Dispensary Map"
        />
        <div className="finderCTA">
          <div className="text-md text-center col font-semibold">
            Looking to find a nearby dispensary?
          </div>
          <a
            data-testid="view-map-mobile"
            className="button button--tertiary bg-white mt-xl flex items-center justify-center text-sm"
            href={dispensariesLink}
            onClick={() =>
              publishEvent({
                action: Action.click,
                category: Category.viewDispensaryMap,
              })
            }
          >
            view dispensary map
          </a>
        </div>
      </div>
      <style jsx>{`
        .finderCTA {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          font-weight: 600;
        }

        @media screen and (max-width: 769px) {
          .finderCTA {
            padding: 0 1rem;
            width: 100%;
          }
        }
      `}</style>
    </>
  );
};

export default DispensaryResults;
